import apiClient from "./index";
import { Package } from "./packageAPI";

/** Nested application on user payment list (from GET /payments/my-payments). */
export type PaymentApplicationSummary = {
  id: number;
  beneficiaryName: string;
  visaType: string;
  stage: string;
};

/** Set on payments created when Stripe checkout included a USCIS filing line item. */
export type PaymentUscisFeeMeta = {
  formGroup?: string;
  amount?: number | string;
  currency?: string;
  breakdown?: unknown;
};

export type PaymentMetadata = {
  stripeSessionUrl?: string;
  beneficiaryName?: string;
  visaType?: string;
  uscisFee?: PaymentUscisFeeMeta | string;
  [key: string]: unknown;
};

/**
 * Payment interface
 */
export interface Payment {
  id: number;
  applicationId: number;
  application?: PaymentApplicationSummary | null;
  package: Package;
  status: string;
  amount: number;
  currency: string;
  stripeSessionId: string;
  stripePaymentIntentId?: string;
  metadata?: PaymentMetadata | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * User-facing payment reference: always the Stripe Checkout session id.
 * Single stable value (does not switch to payment intent after webhooks).
 */
export function getPaymentTransactionDisplayId(
  payment: Pick<Payment, "stripeSessionId">
): string {
  return (payment.stripeSessionId || "").trim();
}

/**
 * Create Checkout Session Response
 */
export interface CheckoutSessionResponse {
  paymentId: number;
  sessionId: string;
  sessionUrl: string;
  status: string;
}

/**
 * Create a Stripe checkout session
 * @param applicationId - The application ID
 * @param packageId - The package ID
 * @param successUrl - URL to redirect after successful payment
 * @param cancelUrl - URL to redirect if payment is cancelled
 * @returns Promise<CheckoutSessionResponse>
 */
export const createCheckoutSession = async (
  applicationId: number,
  packageId: number,
  successUrl?: string,
  cancelUrl?: string
): Promise<CheckoutSessionResponse> => {
  try {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: CheckoutSessionResponse;
    }>("/payments/create-checkout-session", {
      applicationId,
      packageId,
      successUrl:
        successUrl ||
        `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}&payment_status=success`,
      cancelUrl: cancelUrl || `${window.location.origin}/dashboard?payment_status=cancel`,
    });

    return response.data.data;
  } catch (error: any) {
    // Client worklist #79. The server refuses payment until the eligibility
    // agreement is given. The package page asks for it itself, but checkout
    // can also start from the USCIS-fee pages, which cannot show it — so send
    // the applicant to where it is shown rather than leave a generic error.
    if (error?.response?.data?.code === "ELIGIBILITY_ACKNOWLEDGEMENT_REQUIRED") {
      window.location.assign(`/subscription-package?applicationId=${applicationId}`);
    }
    console.error("Error creating checkout session:", error);
    throw error;
  }
};

/**
 * Get payment by session ID
 * @param sessionId - The Stripe session ID
 * @returns Promise<Payment>
 */
export const getPaymentBySessionId = async (
  sessionId: string
): Promise<Payment> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: Payment;
    }>(`/payments/session/${sessionId}`);

    return response.data.data;
  } catch (error) {
    console.error("Error fetching payment:", error);
    throw error;
  }
};

/**
 * Confirm payment after successful checkout
 * @param sessionId - The Stripe session ID
 * @returns Promise<Payment>
 */
export const confirmPayment = async (sessionId: string): Promise<Payment> => {
  try {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: Payment;
    }>("/payments/confirm", {
      sessionId,
    });

    return response.data.data;
  } catch (error) {
    console.error("Error confirming payment:", error);
    throw error;
  }
};

/**
 * Payment Stats Interface
 */
export interface PaymentStats {
  totalPayments: number;
  totalAmount: number;
  successfulPayments: number;
  pendingPayments: number;
  failedPayments: number;
}

/**
 * Get all payments for the current user
 * @returns Promise<Payment[]>
 */
export const getUserPayments = async (): Promise<Payment[]> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: Payment[];
    }>("/payments/my-payments");

    return response.data.data;
  } catch (error) {
    console.error("Error fetching user payments:", error);
    throw error;
  }
};

/**
 * Get payment statistics for current user
 * @returns Promise<PaymentStats>
 */
export const getUserPaymentStats = async (): Promise<PaymentStats> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: PaymentStats;
    }>("/payments/my-stats");

    return response.data.data;
  } catch (error) {
    console.error("Error fetching payment stats:", error);
    throw error;
  }
};

/**
 * Get payment by ID
 * @param paymentId - The payment ID
 * @returns Promise<Payment>
 */
export const getPaymentById = async (paymentId: number): Promise<Payment> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: Payment;
    }>(`/payments/${paymentId}`);

    return response.data.data;
  } catch (error) {
    console.error("Error fetching payment by ID:", error);
    throw error;
  }
};
